<template>
  <ModuleContainer v-bind="bindModule">
    <template #btn>
      <MapControlButton
        v-if="hasBtn"
        icon="mdi-magnify-plus"
        :active="c_show"
        @click="togglePanel"
        tooltip="Tìm kiếm nâng cao"
      />
    </template>

    <template #draggable="props">
      <DraggablePopup
        v-bind="props"
        id="advanced-search-panel"
        :show.sync="c_show"
        title="Tìm kiếm & Lọc nâng cao"
        :width="400"
        :height="440"
        :top="
          popupTop !== undefined
            ? popupTop
            : props.top !== undefined
            ? props.top
            : 10
        "
        :left="
          popupLeft !== undefined
            ? popupLeft
            : props.right !== undefined
            ? undefined
            : 50
        "
        :right="popupRight !== undefined ? popupRight : props.right"
      >
        <div class="pa-3 d-flex flex-column">
          <v-row dense class="mb-2">
            <v-col cols="6">
              <!-- Chọn Layer -->
              <v-autocomplete
                v-model="searchForm.layer_id"
                :items="displayLayers"
                item-text="name"
                item-value="id"
                label="Lớp dữ liệu"
                placeholder="Tất cả lớp"
                outlined
                dense
                hide-details
                class="visible-border-select"
                clearable
                @change="onLayerChange"
              ></v-autocomplete>
            </v-col>
            <v-col cols="6">
              <!-- Chọn Tỉnh / Thành phố -->
              <v-autocomplete
                v-model="searchForm.province_id"
                :items="provinceList"
                item-text="ten"
                item-value="id"
                label="Tỉnh / Thành phố"
                placeholder="Tất cả"
                outlined
                dense
                hide-details
                class="visible-border-select"
                clearable
                :loading="provinceLoading"
              ></v-autocomplete>
            </v-col>
          </v-row>

          <!-- Từ khóa -->
          <v-text-field
            v-model="searchForm.keyword"
            label="Từ khóa tìm kiếm (tên, mô tả...)"
            outlined
            dense
            hide-details
            clearable
            class="mb-3 visible-border-select"
            append-icon="mdi-magnify"
            @keyup.enter="doSearch(1)"
          ></v-text-field>

          <v-tabs v-model="tab" dense>
            <v-tab>Thuộc tính</v-tab>
            <v-tab>Không gian</v-tab>
          </v-tabs>

          <v-tabs-items
            v-model="tab"
            class="flex-grow-0 mt-3"
            style="overflow: visible;"
          >
            <v-tab-item>
              <div v-if="!searchForm.layer_id" class="text-center grey--text">
                <v-icon size="48" color="grey lighten-1"
                  >mdi-filter-variant</v-icon
                >
                <div class="mt-2 text-body-2 font-weight-bold"
                  >Bạn đang tìm kiếm trên Tất cả các lớp.</div
                >
                <div class="text-caption"
                  >Vui lòng chọn 1 lớp dữ liệu cụ thể phía trên để có thể lọc
                  theo thuộc tính riêng của lớp đó.</div
                >
              </div>
              <QueryBuilder
                v-else
                ref="queryBuilder"
                :layer-id="searchForm.layer_id"
                :fields="layerFields"
                @update:filters="(val) => (searchForm.attribute_filters = val)"
              />
            </v-tab-item>
            <v-tab-item>
              <SpatialFilterTools
                ref="spatialTools"
                @update:spatial="(val) => (searchForm.spatial_filter = val)"
              />
            </v-tab-item>
          </v-tabs-items>

          <v-btn
            color="primary"
            class="mt-3"
            @click="doSearch(1)"
            :loading="loading"
            :disabled="displayLayers.length === 0"
          >
            Tìm kiếm
          </v-btn>

          <v-divider class="my-3"></v-divider>
        </div>
      </DraggablePopup>
    </template>
  </ModuleContainer>
</template>

<script>
import { DraggablePopup } from '@components/draggable'
import MapControlButton from '@components/Map/control/MapControlButton.vue'
import ModuleMixin from '@components/Map/mixins/ModuleMixin'
import ModuleContainer from '@components/Map/ModuleContainer.vue'
import { API_MAP_SEARCH, API_LAYER } from '@api'
import sdk from '@/src/service/auth-sdk'
import get from 'lodash/get'

import QueryBuilder from './QueryBuilder.vue'
import SpatialFilterTools from './SpatialFilterTools.vue'

export default {
  name: 'AdvancedSearchPanel',
  components: {
    DraggablePopup,
    MapControlButton,
    ModuleContainer,
    QueryBuilder,
    SpatialFilterTools,
  },
  mixins: [ModuleMixin],
  props: {
    hasBtn: { type: Boolean, default: true },
    layers: { type: Array, default: () => [] },
    popupTop: { type: Number, default: undefined },
    popupLeft: { type: [Number, String], default: undefined },
    popupRight: { type: [Number, String], default: undefined },
    headless: { type: Boolean, default: false },
  },
  data: () => ({
    c_show: false,
    tab: 0,
    loading: false,
    detailLoadingId: null,
    showMultiExportDialog: false,
    layerFields: [],
    provinceList: [],
    provinceLoading: false,
    searchForm: {
      layer_id: null,
      province_id: null,
      keyword: '',
      attribute_filters: [],
      spatial_filter: null,
      page: 1,
      limit: 5,
    },
    results: [],
    total: 0,
  }),
  created() {
    this.fetchProvinces()
  },
  computed: {
    displayLayers() {
      // Format layers prop for autocomplete
      return this.layers
        .map((l) => ({
          id: get(l, 'info.id', l.id),
          name: l.name || l.title || 'Không tên',
        }))
        .filter((l) => l.id)
    },
  },
  methods: {
    togglePanel() {
      this.c_show = !this.c_show
    },
    async fetchProvinces() {
      this.provinceLoading = true
      try {
        const res = await API_MAP_SEARCH.getProvinces()
        if (res && res.data) {
          this.provinceList = res.data
        } else if (Array.isArray(res)) {
          this.provinceList = res
        }
      } catch (e) {
        console.error('Lỗi lấy danh sách tỉnh thành:', e)
      } finally {
        this.provinceLoading = false
      }
    },
    async onLayerChange(id) {
      this.searchForm.attribute_filters = []
      this.results = []
      this.total = 0
      this.layerFields = []
      if (id) {
        try {
          let res = await API_LAYER.getLayerFields(id)
          if (res && !Array.isArray(res) && Array.isArray(res.data)) {
            res = res.data
          }
          if (Array.isArray(res)) {
            this.layerFields = res
              .map((f) => ({
                name: f.name || f.code,
                code: f.code || f.name,
                type: f.type,
              }))
              .filter((f) => f.type !== 'geometry')
          }
        } catch (e) {
          console.error('Lỗi lấy danh sách trường layer:', e)
        }
      }
    },
    getSearchPayload() {
      let lMap = {}
      this.displayLayers.forEach((l) => {
        lMap[l.id] = l.name
      })
      return {
        keyword: this.searchForm.keyword,
        province_id: this.searchForm.province_id || null,
        spatial_filter: this.searchForm.spatial_filter,
        attribute_filters: this.searchForm.attribute_filters,
        layer_ids: this.searchForm.layer_id
          ? [this.searchForm.layer_id]
          : this.displayLayers.map((l) => l.id),
        layer_names: lMap,
        test_multi: true,
      }
    },
    async onSelectFeature(item) {
      this.detailLoadingId = item.id
      try {
        let res = await sdk.request({
          url: 'map/detail',
          method: 'post',
          data: {
            data_type: 'nbds_vector_layer',
            data_id: item.layer_id || this.searchForm.layer_id,
            feature_id: item.id,
          },
        })
        this.$emit('click:select', { ...res.data, feature: item })
      } catch (error) {
        console.error('Lỗi lấy chi tiết:', error)
      } finally {
        this.detailLoadingId = null
      }
    },
    async doSearch(page = 1) {
      if (this.displayLayers.length === 0) {
        this.$store.dispatch('notify/warning', {
          message: 'Không có lớp dữ liệu nào trên bản đồ để tìm kiếm.',
        })
        return
      }

      this.searchForm.page = page
      this.loading = true
      this.$emit('update:loading', true)
      try {
        const payload = this.getSearchPayload()
        payload.page = this.searchForm.page
        payload.limit = this.searchForm.limit

        const res = await API_MAP_SEARCH.advancedSearch(payload)
        if (res) {
          this.results = res.list || []
          this.total = res.pagination ? res.pagination.total : 0
          this.$emit('results-updated', {
            results: this.results,
            total: this.total,
            page: this.searchForm.page,
            limit: this.searchForm.limit,
            searchPayload: payload,
            layerFields: this.layerFields,
          })
        }
      } catch (err) {
        this.$store.dispatch('notify/error', { message: 'Lỗi tìm kiếm' })
        console.error(err)
      } finally {
        this.loading = false
        this.$emit('update:loading', false)
      }
    },
  },
}
</script>
